import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { FeedbackInfo } from "./FeedbackInfo";
import { NotesSection } from "./NotesSection";
import { useActivityLogs } from "@/components/pages/dashboard/hooks/useActivityLogs";
import {
  getActivityStreamChannelId,
  type ActivityLog,
} from "@/services/activityLogService";
import {
  formatAction,
  formatReservationLogMessage,
  formatTime,
} from "@/utils/formatActivityLogDisplay";

const RESERVATION_LOG_PAGE_SIZE = 4;

const getChatActivityStatusLabel = (
  activity: ActivityLog["activity"],
  translate: (key: string) => string,
): string => {
  if (activity.status) {
    return activity.status;
  }
  if (activity.action === "channel_resolved") {
    return translate("reservation.otherSection.channelActions.channelResolved");
  }
  if (activity.action === "channel_unresolved") {
    return translate(
      "reservation.otherSection.channelActions.channelUnresolved",
    );
  }
  return formatAction(activity.action);
};

interface OtherInfoProps {
  bookingId?: string;
  guestId?: string;
  notes?: string[];
  guestNotes?: string[];
}

interface ReservationLogsBodyProps {
  bookingId: string | undefined;
  reservationLogsLoading: boolean;
  reservationLogs: ActivityLog[];
  paginatedLogs: ActivityLog[];
  logPage: number;
  setLogPage: React.Dispatch<React.SetStateAction<number>>;
  totalLogPages: number;
  logPageNumbers: number[];
}

const ReservationLogsBody: React.FC<ReservationLogsBodyProps> = ({
  bookingId,
  reservationLogsLoading,
  reservationLogs,
  paginatedLogs,
  logPage,
  setLogPage,
  totalLogPages,
  logPageNumbers,
}) => {
  const { t } = useTranslation();

  if (!bookingId) {
    return (
      <p className="text-muted-foreground px-4 py-3 text-xs">
        {t("reservation.otherSection.noBookingForLogs")}
      </p>
    );
  }

  const showLogsLoading =
    reservationLogsLoading && reservationLogs.length === 0;
  if (showLogsLoading) {
    return (
      <p className="text-muted-foreground px-4 py-3 text-xs">
        {t("reservation.otherSection.logsLoading")}
      </p>
    );
  }

  if (reservationLogs.length === 0) {
    return (
      <p className="text-muted-foreground px-4 py-3 text-xs">
        {t("reservation.otherSection.noLogs")}
      </p>
    );
  }

  return (
    <>
      <div className="text-muted-foreground grid grid-cols-[100px_1fr] gap-6 border-b px-4 py-3 text-xs font-medium">
        <div className="text-left">{t("billing.time")}</div>
        <div className="text-right">{t("billing.logs")}</div>
      </div>
      <div className="divide-y divide-neutral-200 border-b">
        {paginatedLogs.map((entry) => (
          <div
            key={entry.id}
            className="grid grid-cols-[100px_1fr] gap-6 px-4 py-3 text-xs"
          >
            <div className="text-foreground">{formatTime(entry.createdAt)}</div>
            <div className="text-foreground text-right">
              {formatReservationLogMessage(entry)}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          disabled={logPage <= 1}
          onClick={() => setLogPage((p) => Math.max(1, p - 1))}
          className={cn(
            "text-foreground text-sm font-medium",
            logPage <= 1 && "pointer-events-none opacity-50",
          )}
        >
          {"< "}
          {t("reservation.otherSection.previous")}
        </button>
        <div className="flex items-center gap-1">
          {logPageNumbers.map((pageNum) => (
            <button
              key={pageNum}
              type="button"
              onClick={() => setLogPage(pageNum)}
              className={cn(
                "flex size-9 items-center justify-center rounded-md text-sm font-medium",
                logPage === pageNum
                  ? "bg-neutral-50"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {pageNum}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={logPage >= totalLogPages}
          onClick={() => setLogPage((p) => Math.min(totalLogPages, p + 1))}
          className={cn(
            "text-foreground text-sm font-medium",
            logPage >= totalLogPages && "pointer-events-none opacity-50",
          )}
        >
          {t("reservation.otherSection.next")}
          {" >"}
        </button>
      </div>
    </>
  );
};

interface ChatsBodyProps {
  bookingId: string | undefined;
  channelLogsLoading: boolean;
  chatThreads: ActivityLog[];
  onOpenChat: (log: ActivityLog) => void;
}

const ChatsBody: React.FC<ChatsBodyProps> = ({
  bookingId,
  channelLogsLoading,
  chatThreads,
  onOpenChat,
}) => {
  const { t } = useTranslation();

  if (!bookingId) {
    return (
      <p className="text-muted-foreground px-2 pb-2 text-xs">
        {t("reservation.otherSection.noBookingForChats")}
      </p>
    );
  }

  const showChatsLoading = channelLogsLoading && chatThreads.length === 0;
  if (showChatsLoading) {
    return (
      <p className="text-muted-foreground px-2 pb-2 text-xs">
        {t("reservation.otherSection.chatsLoading")}
      </p>
    );
  }

  if (chatThreads.length === 0) {
    return (
      <p className="text-muted-foreground px-2 pb-2 text-xs">
        {t("reservation.otherSection.noChats")}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2 pb-2">
      {chatThreads.map((thread) => {
        const channelId = getActivityStreamChannelId(thread.activity);
        const statusLabel = getChatActivityStatusLabel(thread.activity, t);
        const canOpen = Boolean(channelId);
        return (
          <li key={thread.id}>
            <button
              type="button"
              disabled={!canOpen}
              onClick={() => onOpenChat(thread)}
              className={cn(
                "text-foreground flex w-full items-center gap-3 rounded-lg border border-neutral-200 px-2 py-1 text-left text-sm",
                canOpen
                  ? "cursor-pointer hover:bg-neutral-50"
                  : "cursor-not-allowed opacity-60",
              )}
            >
              <ChevronDown
                className="text-primary size-4 shrink-0"
                aria-hidden
              />
              <span>
                {statusLabel} | {formatTime(thread.createdAt)} |{" "}
                {typeof thread.activity?.participantLabel === "string" &&
                thread.activity.participantLabel
                  ? thread.activity.participantLabel
                  : t("reservation.otherSection.chatUnknownParticipant")}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export const OtherInfo: React.FC<OtherInfoProps> = ({
  bookingId,
  guestId,
  notes,
  guestNotes,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasFeedbacksAccess } = useAccessCapabilities();
  const [logPage, setLogPage] = useState(1);

  const reservationLogFilters = bookingId
    ? {
        bookingId,
        limit: 100,
        sort: "desc" as const,
      }
    : undefined;

  const channelLogFilters = bookingId
    ? {
        bookingId,
        service: ["ChannelUpdate"],
        limit: 100,
        sort: "desc" as const,
      }
    : undefined;

  const { logs: rawBookingLogs, isLoading: reservationLogsLoading } =
    useActivityLogs(reservationLogFilters);

  const { logs: channelUpdateLogs, isLoading: channelLogsLoading } =
    useActivityLogs(channelLogFilters);

  const reservationLogs = useMemo(
    () => rawBookingLogs.filter((log) => log.service !== "ChannelUpdate"),
    [rawBookingLogs],
  );

  const chatThreads = useMemo(
    () =>
      [...channelUpdateLogs].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [channelUpdateLogs],
  );

  const totalLogPages = Math.max(
    1,
    Math.ceil(reservationLogs.length / RESERVATION_LOG_PAGE_SIZE),
  );

  const paginatedLogs = useMemo(() => {
    const start = (logPage - 1) * RESERVATION_LOG_PAGE_SIZE;
    return reservationLogs.slice(start, start + RESERVATION_LOG_PAGE_SIZE);
  }, [logPage, reservationLogs]);

  const logPageNumbers = useMemo(
    () => Array.from({ length: totalLogPages }, (_, index) => index + 1),
    [totalLogPages],
  );

  useEffect(() => {
    setLogPage(1);
  }, [bookingId]);

  useEffect(() => {
    if (logPage > totalLogPages) {
      setLogPage(totalLogPages);
    }
  }, [logPage, totalLogPages]);

  const openChat = (log: ActivityLog) => {
    const channelId = getActivityStreamChannelId(log.activity);
    if (channelId) {
      navigate(`/chats?activeChannel=${encodeURIComponent(channelId)}`);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <Accordion
        type="multiple"
        defaultValue={["feedback", "reservation-logs", "chats"]}
        className="flex w-full flex-col gap-4"
      >
        <AccordionItem value="reservation-logs" className="rounded-lg px-2">
          <AccordionTrigger>
            {t("reservation.otherSection.reservationLogs")}
          </AccordionTrigger>
          <AccordionContent>
            <div className="pb-2">
              <ReservationLogsBody
                bookingId={bookingId}
                reservationLogsLoading={reservationLogsLoading}
                reservationLogs={reservationLogs}
                paginatedLogs={paginatedLogs}
                logPage={logPage}
                setLogPage={setLogPage}
                totalLogPages={totalLogPages}
                logPageNumbers={logPageNumbers}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="chats" className="rounded-lg px-2">
          <AccordionTrigger>
            {t("reservation.otherSection.chats")}
          </AccordionTrigger>
          <AccordionContent>
            <ChatsBody
              bookingId={bookingId}
              channelLogsLoading={channelLogsLoading}
              chatThreads={chatThreads}
              onOpenChat={openChat}
            />
          </AccordionContent>
        </AccordionItem>
        {hasFeedbacksAccess && bookingId && guestId && (
          <AccordionItem value="feedback" className="rounded-lg px-2">
            <AccordionTrigger>
              {t("reservation.feedbackSection.title")}
            </AccordionTrigger>
            <AccordionContent className="px-3">
              <div id="feedback">
                <FeedbackInfo bookingId={bookingId} guestId={guestId} />
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      <NotesSection
        bookingId={bookingId}
        notes={notes}
        guestNotes={guestNotes}
      />
    </div>
  );
};
